import _ from 'lodash';
import * as uuid from 'uuid';
import fetch from 'fetch-retry';
import Config from '../../../AppConfig';
import auth from './auth-client';

const HEADER_NAB_REQUEST_ID = 'x-nab-requestid';
const HEADER_AUTHORIZATION = 'Authorization';
const X_NAB_USER_TOKEN = 'x-nab-usertoken';
const CONTENT_TYPE_NAME = 'content-type';
const CONTENT_TYPE_VALUE = 'application/json';
const AUTH_TOKEN_PREFIX = 'Bearer ';
const RETRY_URLS = [
	Config.mlcApiPrefix + '/plans/.*/profile$',
	Config.mlcApiPrefix + '/advisers/.*/epiAccounts$',
];
const RETRY_DELAY_MS = 1000;

export type Options = {
	withAuth: boolean;
	noGateway?: boolean;
	includeCredentials?: boolean;
};

export type AuthToken = {
	type: string;
	name: string;
	value: string;
	expires: string;
};

export type ErrorResponse = {
	ok: boolean;
	status: number;
	message?: string;
	errorId?: string;
};

export const get = async (url: string, options?: Options) => {
	return await coreFetch(url, options);
};

export const post = async (url: string, payload: any, options?: Options) => {
	return await coreFetch(url, options, 'post', payload);
};

export const put = async (url: string, payload: any, options?: Options) => {
	return await coreFetch(url, options, 'put', payload);
};

const coreFetch = async (
	url: string,
	options?: Options,
	method: string = 'get',
	body?: any
) => {
	const headers = new Headers();
	headers.append(CONTENT_TYPE_NAME, CONTENT_TYPE_VALUE);

	// Add NAB Request Id
	headers.append(HEADER_NAB_REQUEST_ID, getXNabRequestId());

	if (options && options.withAuth) {
		const authToken = AUTH_TOKEN_PREFIX + (await auth.getAccessTokenSilently());
		// source code changes in MLCG-563
		headers.append(HEADER_AUTHORIZATION, authToken);
	}

	if (options && options.noGateway) {
		headers.append(X_NAB_USER_TOKEN, getXNabUserToken());
	}

	return new Promise(async (resolve, reject) => {
		let init: {
			headers: any;
			method: string;
			body?: any;
			retryOn: any;
			retryDelay: number;
			credentials?: RequestCredentials;
		} = {
			headers,
			method: method,
			retryOn: (attempt: number, error: any, response: any) =>
				retryonFunc(attempt, error, response, url),
			retryDelay: RETRY_DELAY_MS,
		};
		if (method === 'post' || method === 'put') {
			init = { ...init, body: JSON.stringify(body) };
		}

		if (options && options.includeCredentials) {
			init = { ...init, credentials: 'same-origin' };
		}

		try {
			let response = await fetch(url, init);
			if (response.ok) {
				try {
					resolve(await response.json());
				} catch (err) {
					// There may not be a response body
					resolve();
				}
			} else {
				const toReturn: ErrorResponse = {
					ok: false,
					status: response.status,
				};
				if (response.status === 400) {
					const result = await response.json();
					toReturn.message = result.message;
					toReturn.errorId = result.errorId;
				}
				reject(toReturn);
			}
		} catch (error) {
			reject();
		}
	});
};

export const retryonFunc = (
	attempt: number,
	error: any,
	response: any,
	url: string
) => {
	const urlMatch = _.find(RETRY_URLS, item => new RegExp(item).test(url));

	if (urlMatch && (!response.ok || error !== null) && attempt < 1) {
		return true;
	}
};

const getXNabUserToken = (): any => {
	const cookieObject = _(document.cookie)
		.split(';')
		.map(item => _.trim(item))
		.map(item => _.split(item, '='))
		.fromPairs()
		.value();

	return cookieObject.mlcToken;
};

const getXNabRequestId = (): string => {
	let xNabRequestId = localStorage.getItem(HEADER_NAB_REQUEST_ID);

	if (!xNabRequestId) {
		xNabRequestId = uuid.v4();
		localStorage.setItem(HEADER_NAB_REQUEST_ID, xNabRequestId);
	}

	return xNabRequestId;
};
